/**
 * Tabs del panel contextual de la Vista 360 (Fase 2b):
 * Estado SII · Relacionados · Comprobante · Cobranza · Factoring · Adjuntos · Historial.
 */
import { todayISO } from '../../../../../lib/utils'
import { useState } from 'react'
import { DataTable } from '@/components/ds/DataTable'
import { useQueryClient } from '@tanstack/react-query'
import {
  Loader2, CheckCircle2, XCircle, Info, Mail, FileText, Download, ExternalLink,
  RotateCcw, Plus, MessageSquarePlus, Pencil,
} from 'lucide-react'
import { fmtDate, fmtDateTime } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { useApproveVoucher, useReverseVoucher } from '@/modules/accounting/hooks/useAccounting'
import { VoucherForm } from '@/modules/accounting/components/VoucherForm'
import type { SaleDocument, CollectionInvoice } from '@/modules/sales/hooks/useSales'
import { PaymentModal } from '@/modules/sales/components/PaymentModal'
import { CollectionLogModal } from '@/modules/sales/components/CollectionLogModal'
import {
  useDocumentRelated, useDocumentVoucher, useDocumentCollection, useDocumentFactoring,
  type CollectionResponse,
} from './use360Queries'
import { Timeline } from '@/components/ds/Timeline'
import { tonoSolido } from '@/components/ds/tonos'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { PAYMENT_LINK_STATUS } from '@/components/ds/statusCatalogs'

type Fmt = (v: string | number) => string
export interface TabNotify { success: (m: string) => void; error: (m: string) => void }

// Botón de acción compacto para las cabeceras de las tabs.
function TabActionBtn({ label, icon, variant = 'default', onClick, busy }: {
  label: string; icon?: React.ReactNode; variant?: 'default' | 'primary' | 'danger'; onClick: () => void; busy?: boolean
}) {
  const cls = {
    default: 'bg-surface border-divider-strong text-content hover:bg-surface-muted',
    primary: 'bg-primary border-primary text-on-primary hover:bg-primary-hover',
    danger: 'bg-surface border-danger/40 text-danger hover:bg-danger/10',
  }[variant]
  return (
    <button onClick={onClick} disabled={busy} className={`rounded-lg px-2.5 py-1.5 text-[12px] font-semibold inline-flex items-center gap-1.5 border ${cls} disabled:opacity-60`}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : icon}{label}
    </button>
  )
}

// ─── Primitivas compartidas ────────────────────────────────────────────────────

function TabLoading() {
  return <div className="grid place-items-center py-10 text-content-muted"><Loader2 className="animate-spin" /></div>
}
function TabError() {
  return <p className="text-content-muted text-sm py-6 text-center">No se pudo cargar la información.</p>
}
function TabEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-center text-content-subtle py-10 text-[13px]">
      <p className="text-3xl opacity-40 mb-2">∅</p>
      {children}
    </div>
  )
}
function SecLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-[11px] font-extrabold uppercase tracking-wide text-content-subtle mt-4 mb-2 first:mt-0">{children}</p>
}
function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-[12.5px] py-1">
      <span className="text-content-muted">{k}</span>
      <span className="font-semibold tabular-nums text-right">{v}</span>
    </div>
  )
}
function MiniCard({ children }: { children: React.ReactNode }) {
  return <div className="border border-divider rounded-xl p-3 mb-3">{children}</div>
}

// ─── Estado SII y trazabilidad ───────────────────────────────────────────────

const SII_EVENT_LABEL: Record<string, string> = {
  started: 'Emisión iniciada', xml_generated: 'XML generado', xml_signed: 'XML firmado',
  sent: 'Enviado al SII', status_queried: 'Estado consultado', accepted: 'Aceptado por el SII',
  rejected: 'Rechazado por el SII', error: 'Error', email_sent: 'Enviado por correo',
}
/**
 * El color del punto de cada evento.
 *
 * ⚠️ Devuelve un color CSS completo y no una clase, porque el `Timeline` del
 * design system lo pinta en línea. Sale de `tonoSolido`, que resuelve la
 * diferencia entre los tokens que son canales y los que son color compuesto —
 * y de paso evita el `--color-info` azul junto a una familia `info-*` violeta.
 */
function tonoDelEvento(status: string): string {
  if (status === 'success') return tonoSolido('success')
  if (status === 'error') return tonoSolido('danger')
  return tonoSolido('info')
}

export function SiiTimelineTab({ doc, locale }: { doc: SaleDocument; locale: string }) {
  const logs = [...(doc.emission_logs ?? [])].reverse() // más reciente arriba
  const ld = doc.localization_data || {}
  const distributedAt = ld.distributed_to_receiver_at as string | undefined
  const distEmail = ld.distribution_email as string | undefined
  const distSource = ld.distribution_source as string | undefined
  const trackid = (ld.track_id as string) || (ld.trackid as string) || null

  return (
    <div>
      {trackid && <KV k="TrackID SII" v={trackid} />}

      <SecLabel>Distribución al receptor</SecLabel>
      {distributedAt ? (
        <MiniCard>
          <div className="flex items-center gap-2 text-success text-[12.5px] font-semibold mb-1">
            <Mail size={14} /> Distribuido
          </div>
          <KV k="Fecha" v={fmtDateTime(distributedAt, locale)} />
          {distEmail && <KV k="Correo" v={distEmail} />}
          {distSource && <KV k="Fuente" v={distSource} />}
        </MiniCard>
      ) : (
        <p className="text-content-subtle text-[12.5px]">Aún no distribuido al receptor.</p>
      )}

      <SecLabel>Trazabilidad</SecLabel>
      {logs.length === 0 ? (
        <p className="text-content-subtle text-[12.5px]">Sin eventos registrados.</p>
      ) : (
        <Timeline
          items={logs.map((log) => ({
            label: SII_EVENT_LABEL[log.event] ?? log.event,
            // ⚠️ La hora va formateada acá, con el locale del tenant: el
            // componente no debe decidir en qué zona ni con qué formato se lee
            // una fecha — es la lección del QR que salía en UTC.
            time: fmtDateTime(log.created_at, locale),
            // El mensaje es la glosa del SII, y ahora va en su propia caja en
            // vez de como texto suelto: cuando un documento se rechaza, ese
            // texto es lo único que dice por qué.
            note: log.message || undefined,
            tone: tonoDelEvento(log.status),
          }))}
        />
      )}
    </div>
  )
}

// ─── Relacionados (NC/ND + crédito) ───────────────────────────────────────────

export function RelatedTab({ doc, fmt, locale, onNavigate }: { doc: SaleDocument; fmt: Fmt; locale: string; onNavigate: (id: string) => void }) {
  const { data, isLoading, isError } = useDocumentRelated(doc.id, true)
  if (isLoading) return <TabLoading />
  if (isError || !data) return <TabError />

  const hasAny = data.reference_document || data.linked_adjustments.length > 0 || data.generated_credit
  if (!hasAny) return <TabEmpty>Sin documentos relacionados.</TabEmpty>

  return (
    <div>
      {data.reference_document && (
        <>
          <SecLabel>Documento original</SecLabel>
          <button
            onClick={() => onNavigate(data.reference_document!.id)}
            className="w-full text-left border border-divider rounded-xl p-3 mb-3 hover:bg-surface-muted"
          >
            <div className="flex justify-between items-center">
              <span className="font-bold text-[13px]">{data.reference_document.document_type} {data.reference_document.folio || '—'}</span>
              <ExternalLink size={14} className="text-content-muted" />
            </div>
            <KV k="Total" v={fmt(data.reference_document.total)} />
          </button>
        </>
      )}

      {data.linked_adjustments.length > 0 && (
        <>
          <SecLabel>Notas de ajuste (NC / ND)</SecLabel>
          {data.linked_adjustments.map((a) => (
            <button
              key={a.id}
              onClick={() => onNavigate(a.id)}
              className="w-full text-left border border-divider rounded-xl p-3 mb-2 hover:bg-surface-muted"
            >
              <div className="flex justify-between items-center">
                <span className="font-bold text-[13px]">{a.type} {a.folio || '—'}</span>
                <span className={`text-[11px] ${a.type === 'NC' ? 'text-danger' : 'text-warning'}`}>
                  {a.type === 'NC' ? '−' : '+'}{fmt(a.amount)}
                </span>
              </div>
              {a.reason && <div className="text-[12px] text-content-muted mt-0.5">{a.reason}</div>}
              <div className="text-[11px] text-content-subtle mt-0.5">{fmtDate(a.issue_date, locale)}{a.status_display ? ` · ${a.status_display}` : ''}</div>
            </button>
          ))}
          <div className="border-t border-divider mt-2 pt-2">
            <KV k="Total ajustado" v={fmt(data.adjusted_total)} />
          </div>
        </>
      )}

      {data.generated_credit && (
        <>
          <SecLabel>Crédito a favor generado</SecLabel>
          <MiniCard>
            <KV k="Monto" v={fmt(data.generated_credit.amount)} />
            <KV k="Estado" v={data.generated_credit.status_display} />
          </MiniCard>
        </>
      )}
    </div>
  )
}

// ─── Comprobante contable ──────────────────────────────────────────────────────

export function VoucherTab({ doc, fmt, locale, notify, onOpenVoucher }: { doc: SaleDocument; fmt: Fmt; locale: string; notify: TabNotify; onOpenVoucher?: () => void }) {
  const { data, isLoading, isError } = useDocumentVoucher(doc.id, true)
  const qc = useQueryClient()
  const { can } = usePermission()
  const canApprove = can('accounting.vouchers', 'approve')
  const canEdit = can('accounting.vouchers', 'edit')
  const canEditApproved = can('accounting.vouchers', 'edit_approved')
  const approve = useApproveVoucher()
  const reverse = useReverseVoucher()
  const { confirm, modalProps } = useConfirmModal()
  const [reverseOpen, setReverseOpen] = useState(false)
  const [revDate, setRevDate] = useState(todayISO())
  const [revDesc, setRevDesc] = useState('')
  const [editOpen, setEditOpen] = useState(false)

  if (isLoading) return <TabLoading />
  if (isError || !data) return <TabError />
  if (!data.voucher) return <TabEmpty>Este documento aún no tiene comprobante contable.</TabEmpty>

  const v = data.voucher
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['doc360'] })
    qc.invalidateQueries({ queryKey: ['accounting', 'vouchers'] })
  }
  // Editar in-place: borrador con 'edit'; aprobado solo con 'edit_approved' y con
  // confirmación de riesgo (el backend vuelve a validarlo y respeta el bloqueo de período).
  const canEditThis = (v.status === 'draft' && canEdit) || (v.status === 'approved' && canEditApproved)
  const openEdit = () => {
    if (v.status === 'approved') {
      confirm({
        variant: 'danger',
        confirmLabel: 'Editar de todas formas',
        message:
          'Se va a EDITAR un comprobante ya APROBADO. Esto puede descuadrar la contabilidad y ' +
          'afectar balances, EERR, F22/F29 y reportes ya emitidos. El comprobante permanece ' +
          'aprobado y el cambio queda registrado en la bitácora. ¿Continuar?',
        onConfirm: () => setEditOpen(true),
      })
    } else {
      setEditOpen(true)
    }
  }
  const doApprove = () => approve.mutate(v.id, {
    onSuccess: () => { notify.success(`Comprobante ${v.number} aprobado.`); refresh() },
    onError: () => notify.error('No se pudo aprobar el comprobante.'),
  })
  const doReverse = () => reverse.mutate({ id: v.id, date: revDate, description: revDesc || undefined }, {
    onSuccess: () => { notify.success(`Comprobante ${v.number} reversado.`); setReverseOpen(false); refresh() },
    onError: () => notify.error('No se pudo reversar (¿período cerrado?).'),
  })

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-bold text-[14px]">{v.number || 'Comprobante'}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${v.status === 'approved' ? 'bg-success/15 text-success' : 'bg-soft text-content-muted'}`}>
          {v.status_display}
        </span>
      </div>
      <KV k="Fecha" v={v.date ? fmtDate(v.date, locale) : '—'} />
      {v.description && <p className="text-[12px] text-content-muted mt-1 mb-2">{v.description}</p>}

      {/* Acciones según estado: ver completo (drawer in-place) + Aprobar/Reversar */}
      <div className="flex flex-wrap gap-1.5 my-3">
        {onOpenVoucher && (
          <TabActionBtn label="Ver comprobante completo" icon={<ExternalLink size={13} />} onClick={onOpenVoucher} />
        )}
        {canEditThis && (
          <TabActionBtn label="Editar comprobante" icon={<Pencil size={13} />} onClick={openEdit} />
        )}
        {canApprove && v.status === 'draft' && (
          <TabActionBtn label="Aprobar" variant="primary" icon={<CheckCircle2 size={13} />} onClick={doApprove} busy={approve.isPending} />
        )}
        {canApprove && v.status === 'approved' && (
          <TabActionBtn label="Reversar" variant="danger" icon={<RotateCcw size={13} />} onClick={() => setReverseOpen((o) => !o)} />
        )}
      </div>

      {reverseOpen && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-3 space-y-2">
          <p className="text-[12px] font-semibold text-danger">Reversar comprobante</p>
          <label className="block text-[11.5px] text-content-muted">Fecha de reversa
            <input type="date" value={revDate} onChange={(e) => setRevDate(e.target.value)} className="mt-1 w-full rounded-lg border border-input-border bg-input-bg px-2.5 py-1.5 text-[13px]" />
          </label>
          <label className="block text-[11.5px] text-content-muted">Descripción (opcional)
            <input value={revDesc} onChange={(e) => setRevDesc(e.target.value)} placeholder="Motivo de la reversa" className="mt-1 w-full rounded-lg border border-input-border bg-input-bg px-2.5 py-1.5 text-[13px]" />
          </label>
          <TabActionBtn label="Confirmar reversa" variant="danger" icon={<RotateCcw size={13} />} onClick={doReverse} busy={reverse.isPending} />
        </div>
      )}

      <SecLabel>Líneas</SecLabel>
      <DataTable<typeof v.lines[number]>
        columns={[
          { key: 'account', header: 'Cuenta',
            render: ln => (
              <>{ln.account_code ? `${ln.account_code} · ` : ''}{ln.account_name || ln.glosa}</>
            ) },
          { key: 'debit', header: 'Debe', numeric: true,
            render: ln => (Number(ln.debit) ? fmt(ln.debit) : '') },
          { key: 'credit', header: 'Haber', numeric: true,
            render: ln => (Number(ln.credit) ? fmt(ln.credit) : '') },
        ]}
        rows={v.lines}
        rowKey={(_, i) => i}
        minWidth={320}
        surface={false}
        caption="Líneas del asiento contable"
      />

      {editOpen && (
        <VoucherForm
          voucher={{ id: v.id }}
          onClose={() => setEditOpen(false)}
          onSaved={() => { notify.success(`Comprobante ${v.number} actualizado.`); refresh() }}
        />
      )}
      <ConfirmModal {...modalProps} />
    </div>
  )
}

// ─── Cobranza ──────────────────────────────────────────────────────────────────

export function CollectionTab({ doc, fmt, locale, onPayment }: {
  doc: SaleDocument; fmt: Fmt; locale: string
  onPayment: (payment: CollectionResponse['payments'][number]) => void
}) {
  const { data, isLoading, isError } = useDocumentCollection(doc.id, true)
  const qc = useQueryClient()
  const { can } = usePermission()
  const canManage = can('sales.collections', 'edit') || can('sales.collections', 'create')
  const [payOpen, setPayOpen] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  const refresh = () => qc.invalidateQueries({ queryKey: ['doc360'] })

  if (isLoading) return <TabLoading />
  if (isError || !data) return <TabError />

  // Solo facturas aceptadas con saldo admiten registrar pago/gestión.
  const collectible = doc.status === 'accepted'
  const cs = data.collection_status
  const preselected: CollectionInvoice = {
    id: doc.id,
    customer: doc.customer,
    customer_name: doc.customer_name,
    customer_tax_id: (doc as { customer_tax_id?: string }).customer_tax_id ?? '',
    folio: doc.folio ?? null,
    issue_date: doc.issue_date ?? null,
    due_date: doc.due_date ?? null,
    total: String(doc.total ?? data.total),
    paid_amount: '0',
    outstanding: String(data.outstanding_amount),
    collection_status: (cs === 'partial' || cs === 'paid' || cs === 'overdue') ? cs : 'pending',
  }

  return (
    <div>
      {collectible && canManage && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <TabActionBtn label="Registrar pago" variant="primary" icon={<Plus size={13} />} onClick={() => setPayOpen(true)} />
          <TabActionBtn label="Registrar gestión" icon={<MessageSquarePlus size={13} />} onClick={() => setLogOpen(true)} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-2.5 mb-2">
        <div className="bg-soft rounded-lg px-2.5 py-2">
          <div className="text-[10px] uppercase tracking-wide text-content-subtle">Saldo</div>
          <div className="text-[13px] font-bold tabular-nums mt-0.5">{fmt(data.outstanding_amount)}</div>
        </div>
        <div className="bg-soft rounded-lg px-2.5 py-2">
          <div className="text-[10px] uppercase tracking-wide text-content-subtle">Vence</div>
          <div className="text-[13px] font-bold mt-0.5">{data.due_date ? fmtDate(data.due_date, locale) : '—'}</div>
        </div>
      </div>

      <SecLabel>Pagos</SecLabel>
      {data.payments.length === 0 ? (
        <p className="text-content-subtle text-[12.5px]">Sin pagos registrados.</p>
      ) : (
        data.payments.map((p) => (
          <button key={p.id} onClick={() => onPayment(p)} className="block w-full text-left border border-divider rounded-xl p-3 mb-3 hover:bg-surface-muted hover:border-divider-strong">
            <div className="flex justify-between">
              <span className="font-bold text-[13px] tabular-nums">{fmt(p.amount)}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.status === 'confirmed' ? 'bg-success/15 text-success' : 'bg-soft text-content-muted'}`}>{p.status_display}</span>
            </div>
            <KV k="Fecha" v={p.payment_date ? fmtDate(p.payment_date, locale) : '—'} />
            <KV k="Método" v={p.payment_method_display} />
            {p.bank_account && <KV k="Banco" v={p.bank_account} />}
            {p.reference && <KV k="Referencia" v={p.reference} />}
          </button>
        ))
      )}

      <SecLabel>Gestiones de cobranza</SecLabel>
      {data.collection_logs.length === 0 ? (
        <p className="text-content-subtle text-[12.5px]">Sin gestiones.</p>
      ) : (
        data.collection_logs.map((l) => (
          <MiniCard key={l.id}>
            <div className="flex justify-between items-center">
              <span className="font-bold text-[12.5px]">{l.action_type_display}</span>
              <span className="text-[11px] text-content-subtle">{fmtDate(l.created_at, locale)}</span>
            </div>
            {l.description && <p className="text-[12px] text-content-muted mt-1">{l.description}</p>}
            {l.result && <p className="text-[11.5px] text-content-subtle mt-0.5">Resultado: {l.result}</p>}
          </MiniCard>
        ))
      )}

      {data.payment_links.length > 0 && (
        <>
          <SecLabel>Links de pago</SecLabel>
          {data.payment_links.map((pl) => (
            <MiniCard key={pl.id}>
              <div className="flex justify-between items-center">
                <span className="font-bold text-[12.5px]">{pl.gateway || 'Link'} · {fmt(pl.amount)}</span>
                <StatusBadge status={pl.status} catalog={PAYMENT_LINK_STATUS} />
              </div>
              {pl.url && (
                <a href={pl.url} target="_blank" rel="noreferrer" className="text-primary text-[12px] inline-flex items-center gap-1 mt-1">
                  <ExternalLink size={12} /> Abrir link
                </a>
              )}
            </MiniCard>
          ))}
        </>
      )}

      {payOpen && (
        <PaymentModal
          preselectedInvoice={preselected}
          onClose={() => { setPayOpen(false); refresh() }}
        />
      )}
      {logOpen && (
        <CollectionLogModal
          documentId={doc.id}
          customerName={doc.customer_name}
          folio={doc.folio ?? ''}
          outstanding={String(data.outstanding_amount)}
          onClose={() => { setLogOpen(false); refresh() }}
        />
      )}
    </div>
  )
}

// ─── Factoring / Cesión ────────────────────────────────────────────────────────

export function FactoringTab({ doc, fmt, locale }: { doc: SaleDocument; fmt: Fmt; locale: string }) {
  const { data, isLoading, isError } = useDocumentFactoring(doc.id, true)
  if (isLoading) return <TabLoading />
  if (isError || !data) return <TabError />
  if (data.operations.length === 0) return <TabEmpty>Este documento no ha sido cedido a factoring.</TabEmpty>

  return (
    <div>
      {data.operations.map((op) => (
        <MiniCard key={op.line_id}>
          <div className="flex justify-between items-center mb-1">
            <span className="font-bold text-[13px]">{op.entity?.legal_name || 'Entidad'}</span>
            {op.operation && (
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${op.operation.status === 'paid' ? 'bg-success-surface text-success-content' : 'bg-info-surface text-info-content'}`}>
                {op.operation.status_display}
              </span>
            )}
          </div>
          {op.entity?.tax_id && <KV k="RUT" v={op.entity.tax_id} />}
          {op.operation?.date && <KV k="Fecha" v={fmtDate(op.operation.date, locale)} />}
          <KV k="Monto cedido" v={fmt(op.amount_ceded)} />
          {op.operation?.created_by_name && <KV k="Cedida por" v={op.operation.created_by_name} />}
          {op.operation?.sii_track_id && (
            <>
              <KV k="Estado SII" v={op.operation.sii_estado || 'Aceptada'} />
              <KV k="Id. envío" v={op.operation.sii_track_id} />
              {op.operation.sii_received_at && <KV k="Recepción SII" v={fmtDate(op.operation.sii_received_at, locale)} />}
              {op.identificador_cesion && <KV k="Id. cesión" v={op.identificador_cesion} />}
            </>
          )}
        </MiniCard>
      ))}
    </div>
  )
}

// ─── Adjuntos ──────────────────────────────────────────────────────────────────

export function AttachmentsTab({ doc, locale }: { doc: SaleDocument; locale: string }) {
  const files = [...(doc.attachments ?? []), ...(doc.order_attachments ?? [])]
  if (files.length === 0) return <TabEmpty>Sin adjuntos.</TabEmpty>

  return (
    <div className="flex flex-col gap-2">
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-2.5 border border-divider rounded-lg px-3 py-2">
          <FileText size={16} className="text-content-muted shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold truncate">{f.file_name}</div>
            <div className="text-[11px] text-content-subtle">{fmtDate(f.created_at, locale)}{f.uploaded_by_name ? ` · ${f.uploaded_by_name}` : ''}</div>
          </div>
          {/* Enlace directo a la URL firmada de S3 (mismo patrón que los modales de pedido/documento).
              El re-enrutado previo por apiClient rompía la descarga: le arrancaba el host de S3 y
              re-pedía por la base /api/v1 → ruta inexistente → 404 tragado por el catch. */}
          <a
            href={f.download_url}
            target="_blank"
            rel="noreferrer"
            className="w-8 h-8 rounded-lg border border-divider grid place-items-center text-content-muted hover:bg-surface-muted shrink-0"
            title="Descargar"
          >
            <Download size={14} />
          </a>
        </div>
      ))}
    </div>
  )
}

// ─── Historial ─────────────────────────────────────────────────────────────────

const STATUS_ICON: Record<string, React.ReactNode> = {
  success: <CheckCircle2 size={14} className="text-success" />,
  error: <XCircle size={14} className="text-danger" />,
  info: <Info size={14} className="text-info" />,
}
// Fecha+hora compacta (24h, sin "a. m./p. m." que rompía el layout en el panel).
function compactDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const date = d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false })
  return `${date} · ${time}`
}

export function HistoryTab({ doc, locale }: { doc: SaleDocument; locale: string }) {
  const logs = [...(doc.emission_logs ?? [])].reverse()
  const creator = doc.created_by_name || (doc.created_by ? null : 'Sistema')
  const seller = (doc as { seller_name?: string | null }).seller_name

  // Fila con fecha compacta + actor opcional (label arriba, valor abajo: cabe en el panel).
  const InfoRow = ({ k, when, by }: { k: string; when: string; by?: string | null }) => (
    <div className="py-1.5 border-b border-divider last:border-0">
      <div className="text-[11px] uppercase tracking-wide text-content-subtle">{k}</div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5">
        <span className="text-[12.5px] font-semibold tabular-nums">{when}</span>
        {by && <span className="text-[11.5px] text-content-muted truncate">{by}</span>}
      </div>
    </div>
  )

  return (
    <div>
      <InfoRow k="Creado" when={compactDateTime(doc.created_at, locale)} by={creator} />
      {doc.emitted_at && (
        <InfoRow k="Emitido" when={compactDateTime(doc.emitted_at, locale)} by={doc.emitted_by_name} />
      )}
      {doc.last_sent_at && (
        <InfoRow k="Reenviado al SII" when={compactDateTime(doc.last_sent_at, locale)} by={doc.last_sent_by_name} />
      )}
      {doc.cancelled_at && (
        <InfoRow k="Anulado" when={compactDateTime(doc.cancelled_at, locale)} by={doc.cancelled_by_name} />
      )}
      <InfoRow k="Última actualización" when={compactDateTime(doc.updated_at, locale)} />
      {seller && <InfoRow k="Vendedor" when={seller} />}

      <SecLabel>Eventos</SecLabel>
      {logs.length === 0 ? (
        <p className="text-content-subtle text-[12.5px]">Sin eventos.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {logs.map((log) => (
            <div key={log.id} className="flex items-start gap-2 text-[12.5px]">
              <span className="mt-0.5">{STATUS_ICON[log.status] ?? <Info size={14} />}</span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{SII_EVENT_LABEL[log.event] ?? log.event}</div>
                {log.message && <div className="text-content-muted text-[12px]">{log.message}</div>}
                <div className="flex items-center gap-1.5 text-content-subtle text-[11px] mt-0.5">
                  <span className="tabular-nums">{compactDateTime(log.created_at, locale)}</span>
                  <span className="px-1.5 py-0.5 rounded bg-soft text-[10px]">
                    {log.actor_name ?? 'Sistema'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
